<script lang="ts" setup>
import { computed } from 'vue';
import { useRoute, useRouter } from '#app/composables/router';
import { canReadSpace } from '#layers/access';
import { resolveSettingsNavigation } from '#layers/settings';
import { useCurrentPageNotRendered } from '#layers/spaces/composables/use-current-page-not-rendered';
import { useSpaceEdit } from '#layers/spaces/composables/use-space-edit';
import { useSpaces } from '#layers/spaces/composables/use-spaces';
import {
	API_REFERENCE_ROUTE_NAME,
	ASSETS_ROUTE_NAME,
	CONNECT_ROUTE_NAME,
	CONTENT_ROUTE_NAME,
	DATA_MODEL_ROUTE_NAME,
	PROJECT_SETTINGS_ROUTE_NAME,
} from '#layers/spaces/constants/project-routes';
import { getSpaceRouteParams, isSpaceRoute } from '#layers/spaces/utils/routes';
import { useEntitlement } from '~/composables/use-entitlement';
import { useAuth } from '~/stores/auth';
import { usePermissionsStore } from '~/stores/permissions';
import SpacebarDivider from './Divider.vue';
import SpacebarItem from './Item.vue';

const route = useRoute();
const router = useRouter();
const spaces = useSpaces();
const { can } = useEntitlement();
const { isOrganizationAdmin } = useAuth();
const permissions = usePermissionsStore();
const { createSpace } = useSpaceEdit();
const { currentPageNotRendered } = useCurrentPageNotRendered();

const currentSpaceRouteName = computed(() =>
	route.matched.find((record) => isSpaceRoute(record))?.name,
);

const activeSpaceIds = computed(() => {
	const ids = new Set<string>();
	const currentRouteName = currentSpaceRouteName.value;
	const notRenderedSpaceId = currentPageNotRendered.value?.space?.id;

	if (typeof currentRouteName === 'string') ids.add(currentRouteName);
	if (typeof notRenderedSpaceId === 'string') ids.add(notRenderedSpaceId);

	return ids;
});

const routes = computed(() => router.getRoutes());

function isRouteVisible(routeName: string): boolean {
	const candidate = routes.value.find((record) => record.name === routeName);
	return !candidate?.meta.isVisible || candidate.meta.isVisible({ can });
}

const showDataModel = computed(() => isRouteVisible(DATA_MODEL_ROUTE_NAME));
const showApiReference = computed(() => isRouteVisible(API_REFERENCE_ROUTE_NAME));
const hasVisibleSettings = computed(() => resolveSettingsNavigation({ routes: routes.value, can, isOrganizationAdmin: isOrganizationAdmin.value }).groups.length > 0);
const visibleSpaceItems = computed(() => spaces.value
	.filter((space) => can(canReadSpace(space.id)))
	.map((space) => ({
		active: activeSpaceIds.value.has(space.id),
		icon: space.icon,
		id: space.id,
		label: space.title,
		routeParams: getSpaceRouteParams(space),
	})));
</script>

<template>
	<!-- `border-be border-transparent` aligns spacebar items with the content footer -->
	<nav class="flex min-h-0 w-full flex-1 flex-col items-stretch border-be border-transparent pbe-2">
		<div class="scrollbar-hidden flex min-h-0 grow flex-col items-center gap-2 overflow-y-auto pbe-2 *:shrink-0">
			<SpacebarDivider />

			<SpacebarItem
				:route-name="CONTENT_ROUTE_NAME"
				:label="$t('pages.content.title')"
				icon="lucide:box"
			/>

			<SpacebarItem
				:route-name="ASSETS_ROUTE_NAME"
				:label="$t('common.assets')"
				icon="lucide:folder"
			/>

			<template v-if="permissions.descriptorPending">
				<SpacebarDivider />

				<div
					v-for="n in 2"
					:key="n"
					data-test-id="spacebar-skeleton"
					class="size-9 rounded-md bg-elevated"
				/>
			</template>

			<template v-else>
				<SpacebarDivider v-if="visibleSpaceItems.length > 0" />

				<SpacebarItem
					v-for="space in visibleSpaceItems"
					:key="space.id"
					:route-name="space.id"
					:route-params="space.routeParams"
					:label="space.label"
					:icon="space.icon"
					:active="space.active"
				/>
			</template>

			<SpacebarItem
				:label="$t('space.new_space_tooltip')"
				icon="lucide:plus"
				class="not-hover:opacity-50"
				@click="createSpace"
			/>
		</div>

		<div
			class="flex shrink-0 flex-col items-center gap-2 pbe-1"
		>
			<SpacebarDivider />

			<template v-if="permissions.descriptorPending">
				<div
					v-for="n in 3"
					:key="n"
					data-test-id="spacebar-skeleton"
					class="size-9 rounded-md bg-elevated"
				/>
			</template>

			<template v-else>
				<SpacebarItem
					:route-name="CONNECT_ROUTE_NAME"
					:label="$t('pages.connect.page.page_title')"
					icon="lucide:plug"
				/>

				<SpacebarItem
					v-if="showDataModel"
					:route-name="DATA_MODEL_ROUTE_NAME"
					:label="$t('pages.data_model.title')"
					icon="lucide:database"
				/>

				<SpacebarItem
					v-if="showApiReference"
					:route-name="API_REFERENCE_ROUTE_NAME"
					:label="$t('pages.api_reference.title')"
					icon="lucide:book-open-text"
				/>

				<SpacebarItem
					v-if="hasVisibleSettings"
					:route-name="PROJECT_SETTINGS_ROUTE_NAME"
					:label="$t('pages.settings.title')"
					icon="lucide:settings"
				/>
			</template>
			<slot name="user" />
		</div>
	</nav>
</template>
